// 专用于 MeshPool / ThreeMonitor 的性能时间线采集与统计
type FrameId = string | number | undefined;

interface TimelineRecord {
  frameId?: FrameId;
  wsTs?: number; // websocket onmessage 到达主线程时间
  sendToWorkerTs?: number; // 主线程向 worker postMessage 时间（performance.now 基准）
  workerRecvTs?: number; // worker onmessage 收到时间（worker端写回 meta，可为 performance.now）
  workerDecodeTs?: number; // worker 解包打包完成时间（worker端写回 meta，可为 performance.now）
  mainRecvTs?: number; // 主线程收到 worker 解析后数据时间（performance.now）
  renderTs?: number; // 渲染完成/显示时间（主线程渲染后标记，performance.now）
  // Epoch timestamps (Date.now) — 推荐用于跨线程比较
  wsEpoch?: number;
  sendEpoch?: number;
  workerRecvEpoch?: number;
  workerDecodeEpoch?: number;
  mainRecvEpoch?: number;
  renderEpoch?: number;
  updatedAt: number;
}

class MeshPoolDiagnoser {
  private records = new Map<string, TimelineRecord>();
  // 出站时间映射：frameId -> { wsEpoch, sendEpoch }
  private outboundMap: Map<string, { wsEpoch: number; sendEpoch: number }> =
    new Map();

  pushOutbound(frameId: string | number, sendEpoch: number, wsEpoch: number) {
    try {
      const id = this._id(frameId);
      this.outboundMap.set(id, { sendEpoch, wsEpoch });
      this._pruneOutbound();
    } catch (err) {
      console.warn('meshPoolDiagnoser.pushOutbound failed', err);
    }
  }

  // 当主线程收到 worker 的解析结果时，尝试从队列头取出对应的发送时间并关联到 frameId
  markMainReceived(frameId: FrameId, workerMeta?: any) {
    try {
      const now = performance.now();
      const nowEpoch = Date.now();
      const id = this._id(frameId);
      const rec: TimelineRecord = this._ensure(id);
      rec.mainRecvTs = now;
      rec.mainRecvEpoch = nowEpoch;
      if (workerMeta && typeof workerMeta === 'object') {
        if (typeof workerMeta.workerRecvTs === 'number')
          rec.workerRecvTs = workerMeta.workerRecvTs;
        if (typeof workerMeta.workerRecvEpoch === 'number')
          rec.workerRecvEpoch = workerMeta.workerRecvEpoch;
        if (typeof workerMeta.workerDecodeTs === 'number')
          rec.workerDecodeTs = workerMeta.workerDecodeTs;
        if (typeof workerMeta.workerDecodeEpoch === 'number')
          rec.workerDecodeEpoch = workerMeta.workerDecodeEpoch;
      }

      // 关联出站映射（通过 frameId 精确匹配）
      const out = this.outboundMap.get(id);
      if (out) {
        // outboundMap stores epoch values
        if (typeof out.wsEpoch === 'number') rec.wsEpoch = out.wsEpoch;
        if (typeof out.sendEpoch === 'number') rec.sendEpoch = out.sendEpoch;
        // 匹配后可以删除映射项以节省内存
        this.outboundMap.delete(id);
      }

      rec.updatedAt = now;
    } catch (err) {
      console.warn('meshPoolDiagnoser.markMainReceived failed', err);
    }
  }

  markRenderDisplayed(frameId: FrameId) {
    const now = performance.now();
    const nowEpoch = Date.now();
    const id = this._id(frameId);
    const rec = this._ensure(id);
    rec.renderTs = now;
    rec.renderEpoch = nowEpoch;
    rec.updatedAt = now;
  }

  // 可由 main 在 ws onmessage 时调用以记录 ws 到达时间（可选）
  markWsOnMessage(wsTs?: number) {
    // no-op: use pushOutbound to record ws/send pair
  }

  getAverages(windowMs = 3000) {
    // 返回值说明（单位：毫秒 ms，时间基准使用 performance.now）
    // - count: 参与计算的样本数量（在时间窗内有 renderTs 或 mainRecvTs 的记录条数）
    //
    // 指标逐项含义与计算：
    // - ws_to_send_avg
    //   含义：主线程从收到 WebSocket 消息（wsTs）到将该帧发送给 worker（sendToWorkerTs）的平均耗时。
    //   公式：sendToWorkerTs - wsTs
    //   说明：反映主线程在接收网络数据后到转发给 worker 之间的调度/排队/处理延迟。
    //
    // - ws_to_workerRecv_avg
    //   含义：从 WebSocket 消息到 worker 实际接收到该消息（worker 端记录 workerRecvTs）的平均耗时。
    //   公式：workerRecvTs - wsTs
    //   说明：包含主线程转发开销 + 浏览器将数据调度到 worker 的延迟；若出现负值，通常表示配对不精确或记录缺失。
    //
    // - workerRecv_to_mainRecv_avg
    //   含义：worker 接收到消息到主线程收到 worker 解包结果（mainRecvTs）的平均耗时。
    //   公式：mainRecvTs - workerRecvTs
    //   说明：主要衡量 worker 端解码/打包耗时及回传过程的调度延迟。
    //
    // - mainRecv_to_render_avg
    //   含义：主线程收到 worker 解包结果到完成一次渲染（renderTs）的平均耗时。
    //   公式：renderTs - mainRecvTs
    //   说明：反映主线程在更新 BufferAttribute、调用 Three.js 渲染等环节的耗时与调度延迟。
    //
    // - endToEnd_avg
    //   含义：从 WebSocket 收到消息（wsTs）到最终渲染完成（renderTs）的端到端平均耗时。
    //   公式：renderTs - wsTs
    //   说明：综合上述各段，为用户可感知的整体延迟指标。
    const nowEpoch = Date.now();
    let entries: TimelineRecord[] = [];
    try {
      entries = Array.from(this.records.values()).filter((r) =>
        r.renderEpoch
          ? r.renderEpoch >= nowEpoch - windowMs
          : r.mainRecvEpoch
            ? r.mainRecvEpoch >= nowEpoch - windowMs
            : false
      );
    } catch (err) {
      console.warn('meshPoolDiagnoser.getAverages filter failed', err);
      return {
        count: 0,
        ws_to_send_avg: 0,
        ws_to_workerRecv_avg: 0,
        workerRecv_to_mainRecv_avg: 0,
        mainRecv_to_render_avg: 0,
        endToEnd_avg: 0,
      };
    }

    const stats = {
      count: entries.length,
      ws_to_send_avg: 0,
      ws_to_workerRecv_avg: 0,
      workerRecv_to_mainRecv_avg: 0,
      mainRecv_to_render_avg: 0,
      endToEnd_avg: 0,
    } as any;

    if (entries.length === 0) return stats;

    let a_ws_to_send = 0,
      a_ws_to_worker = 0,
      a_worker_to_main = 0,
      a_main_to_render = 0,
      a_total = 0;
    let valid_ws_to_send = 0,
      valid_ws_to_worker = 0,
      valid_worker_to_main = 0,
      valid_main_to_render = 0,
      valid_total = 0;

    try {
      for (const r of entries) {
        // ws -> send (prefer epoch)
        if (typeof r.wsEpoch === 'number' && typeof r.sendEpoch === 'number') {
          a_ws_to_send += r.sendEpoch - r.wsEpoch;
          valid_ws_to_send++;
        } else if (
          typeof r.wsTs === 'number' &&
          typeof r.sendToWorkerTs === 'number'
        ) {
          a_ws_to_send += r.sendToWorkerTs - r.wsTs;
          valid_ws_to_send++;
        }

        // ws -> workerRecv (prefer epoch)
        if (
          typeof r.wsEpoch === 'number' &&
          typeof r.workerRecvEpoch === 'number'
        ) {
          a_ws_to_worker += r.workerRecvEpoch - r.wsEpoch;
          valid_ws_to_worker++;
        } else if (
          typeof r.wsTs === 'number' &&
          typeof r.workerRecvTs === 'number'
        ) {
          a_ws_to_worker += r.workerRecvTs - r.wsTs;
          valid_ws_to_worker++;
        }

        // workerRecv -> mainRecv (prefer epoch)
        if (
          typeof r.workerRecvEpoch === 'number' &&
          typeof r.mainRecvEpoch === 'number'
        ) {
          a_worker_to_main += r.mainRecvEpoch - r.workerRecvEpoch;
          valid_worker_to_main++;
        } else if (
          typeof r.workerRecvTs === 'number' &&
          typeof r.mainRecvTs === 'number'
        ) {
          a_worker_to_main += r.mainRecvTs - r.workerRecvTs;
          valid_worker_to_main++;
        }

        // mainRecv -> render (prefer epoch)
        if (
          typeof r.mainRecvEpoch === 'number' &&
          typeof r.renderEpoch === 'number'
        ) {
          a_main_to_render += r.renderEpoch - r.mainRecvEpoch;
          valid_main_to_render++;
        } else if (
          typeof r.mainRecvTs === 'number' &&
          typeof r.renderTs === 'number'
        ) {
          a_main_to_render += r.renderTs - r.mainRecvTs;
          valid_main_to_render++;
        }

        // end-to-end (prefer epoch)
        if (
          typeof r.wsEpoch === 'number' &&
          typeof r.renderEpoch === 'number'
        ) {
          a_total += r.renderEpoch - r.wsEpoch;
          valid_total++;
        } else if (
          typeof r.wsTs === 'number' &&
          typeof r.renderTs === 'number'
        ) {
          a_total += r.renderTs - r.wsTs;
          valid_total++;
        }
      }
    } catch (err) {
      console.warn('meshPoolDiagnoser.getAverages calc failed', err);
      return {
        count: entries.length,
        ws_to_send_avg: 0,
        ws_to_workerRecv_avg: 0,
        workerRecv_to_mainRecv_avg: 0,
        mainRecv_to_render_avg: 0,
        endToEnd_avg: 0,
      };
    }

    stats.ws_to_send_avg = valid_ws_to_send
      ? a_ws_to_send / valid_ws_to_send
      : 0;
    stats.ws_to_workerRecv_avg = valid_ws_to_worker
      ? a_ws_to_worker / valid_ws_to_worker
      : 0;
    stats.workerRecv_to_mainRecv_avg = valid_worker_to_main
      ? a_worker_to_main / valid_worker_to_main
      : 0;
    stats.mainRecv_to_render_avg = valid_main_to_render
      ? a_main_to_render / valid_main_to_render
      : 0;
    stats.endToEnd_avg = valid_total ? a_total / valid_total : 0;

    return stats;
  }

  reset() {
    this.records.clear();
    this.outboundMap.clear();
  }

  // internal helpers
  private _ensure(id: string) {
    if (!this.records.has(id))
      this.records.set(id, { frameId: id, updatedAt: performance.now() });
    return this.records.get(id)!;
  }

  private _id(frameId: FrameId) {
    return frameId === undefined || frameId === null
      ? `f_${Date.now()}_${Math.random()}`
      : String(frameId);
  }

  private _pruneOutbound() {
    // 保持队列不过长（避免内存泄露），最多保留 200 项
    if (this.outboundMap.size > 200) {
      // 简单的修剪策略：移除最旧的若干项（Map 迭代顺序为插入顺序）
      const removeCount = this.outboundMap.size - 200;
      const it = this.outboundMap.keys();
      for (let i = 0; i < removeCount; i++) {
        const k = it.next().value;
        this.outboundMap.delete(k || '');
      }
    }
  }
}

const diagnoser = new MeshPoolDiagnoser();
export default diagnoser;
